// ChatApp - chat temps réel Webdesk (WebSocket)
import React, { useEffect, useRef, useState } from 'react';
import { Paper, Typography, Box, TextField, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallIcon from '@mui/icons-material/Call';

// Authentification : récupère le token d'authentification depuis localStorage
function getToken() {
  return window.localStorage.getItem('token');
}
// Ajoute le token dans l'URL WebSocket
function getWsUrl() {
  const token = getToken();
  // Prendre la base depuis REACT_APP_API_URL
  let baseUrl = process.env.REACT_APP_API_URL || '';
  // Remplacer http(s) par ws(s)
  let wsBase = baseUrl.replace(/^http(s?):\/\//, window.location.protocol === 'https:' ? 'wss://' : 'ws://');
  // Nettoyer éventuel slash final
  if (wsBase.endsWith('/')) wsBase = wsBase.slice(0, -1);
  // Ajouter le chemin WebSocket
  let wsUrl = wsBase + '/ws/chat' + (token ? `?token=${encodeURIComponent(token)}` : '');
  return wsUrl;
}

// URL WebSocket pour notifications
function getNotifWsUrl() {
  let baseUrl = process.env.REACT_APP_API_URL || '';
  let wsBase = baseUrl.replace(/^http(s?):\/\//, window.location.protocol === 'https:' ? 'wss://' : 'ws://');
  if (wsBase.endsWith('/')) wsBase = wsBase.slice(0, -1);
  return wsBase + '/ws/notifications';
}

export default function ChatApp() {
  const [messages, setMessages] = useState(() => {
    try {
      const stored = window.localStorage.getItem('chatMessages');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [username, setUsername] = useState(() => {
    const userStr = window.localStorage.getItem('currentUser');
    if (!userStr) return "Anonyme";
    try {
      const userObj = JSON.parse(userStr);
      return userObj.username || "Anonyme";
    } catch {
      return "Anonyme";
    }
  });
  const [users, setUsers] = useState([]); // Liste des utilisateurs connectés
  const [recipient, setRecipient] = useState(''); // Destinataire sélectionné (uuid ou username)
  const [userSearch, setUserSearch] = useState(''); // Terme de recherche utilisateur
  const [showConversation, setShowConversation] = useState(false);
  const ws = useRef(null);
  const listRef = useRef();

  // Ajout des hooks pour WebRTC
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const peerConnectionRef = useRef();
  const [callTarget, setCallTarget] = useState(null);
  const [callError, setCallError] = useState("");

  // --- Vérification expiration JWT ---
  function isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      return Date.now() >= payload.exp * 1000;
    } catch (e) {
      return true;
    }
  }

  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenState, setTokenState] = useState(getToken());

  // Rafraîchit le tokenState si le token change dans localStorage (par exemple, renouvellement ailleurs)
  useEffect(() => {
    function syncToken() {
      setTokenState(getToken());
    }
    window.addEventListener('storage', syncToken);
    return () => window.removeEventListener('storage', syncToken);
  }, []);

  // --- Vérifie l'expiration du token à chaque changement ---
  useEffect(() => {
    if (!tokenState) {
      setTokenExpired(true);
      return;
    }
    if (isTokenExpired(tokenState)) {
      setTokenExpired(true);
      return;
    }
    setTokenExpired(false);
  }, [tokenState]);

  // --- Rafraîchissement du token tant que la connexion WebSocket est active ---
  useEffect(() => {
    if (tokenExpired) return;
    let refreshInterval;
    function refreshTokenIfNeeded() {
      const t = getToken();
      setTokenState(t);
      if (isTokenExpired(t)) {
        setTokenExpired(true);
      }
    }
    // Vérifie toutes les 15 secondes (plus fréquent)
    refreshInterval = setInterval(refreshTokenIfNeeded, 15000);
    return () => clearInterval(refreshInterval);
  }, [tokenExpired]);

  // --- Connexion WebSocket qui se reconnecte si le token change ou est rafraîchi ---
  useEffect(() => {
    if (tokenExpired) return;
    let wsInstance;
    let reconnectTimeout;
    function connectWs() {
      wsInstance = new window.WebSocket(getWsUrl());
      ws.current = wsInstance;
      wsInstance.onopen = () => {
        // Demande explicite la liste des utilisateurs connectés dès l'ouverture
        wsInstance.send(JSON.stringify({ type: 'user_list_request' }));
      };
      wsInstance.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'user_list') {
          setUsers(msg.users);
        } else if (msg.type === 'webrtc-offer' && msg.sender !== username) {
          // Réception d'une offre WebRTC
          handleReceiveOffer(msg);
        } else if (msg.type === 'webrtc-answer' && msg.sender !== username) {
          handleReceiveAnswer(msg);
        } else if (msg.type === 'webrtc-ice' && msg.sender !== username) {
          handleReceiveIce(msg);
        } else {
          setMessages(msgs => {
            const updated = [...msgs, msg];
            try {
              window.localStorage.setItem('chatMessages', JSON.stringify(updated));
              //console.debug('[ChatApp] Message received and stored:', msg);
            } catch (e) {
              console.error('[ChatApp] Failed to store message in localStorage:', e);
            }
            return updated;
          });
          if (msg.sender !== username && msg.recipient === username) {
            if (document.hidden || !showConversation || recipient !== msg.sender) {
              showNotification(msg.sender);
            }
          }
        }
      };
      wsInstance.onclose = () => {
        setMessages(msgs => [...msgs, { sender: 'SYSTEM', content: 'Déconnecté du serveur de chat.', timestamp: Date.now() }]);
        // Tente de se reconnecter si la session du bureau est toujours ouverte
        if (!tokenExpired) {
          reconnectTimeout = setTimeout(connectWs, 2000);
        }
      };
    }
    connectWs();
    return () => {
      if (wsInstance) wsInstance.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [tokenExpired, tokenState, username, showConversation, recipient]);

  // Reload messages from localStorage when the component mounts or when the window/tab becomes visible
  useEffect(() => {
    // Fonction pour charger l'historique depuis l'API backend
    async function fetchMessagesFromApi() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const resp = await fetch(apiUrl.replace(/\/$/, '') + '/chat/messages', {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        });
        if (resp.ok) {
          const msgs = await resp.json();
          setMessages(msgs);
          window.localStorage.setItem('chatMessages', JSON.stringify(msgs));
          //console.debug('[ChatApp] Reloaded messages from backend API on mount/visibility');
        }
      } catch (e) {
        console.error('[ChatApp] Failed to reload messages from backend API:', e);
      }
    }
    // On mount
    fetchMessagesFromApi();
    // On visibility change
    function handleVisibilityChange() {
      if (!document.hidden) {
        fetchMessagesFromApi();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    // Sauvegarde à chaque changement
    try {
      window.localStorage.setItem('chatMessages', JSON.stringify(messages));
    } catch {}
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim()) return;
    if (ws.current && ws.current.readyState === 1) {
      // 1. Envoi du message au chat
      ws.current.send(JSON.stringify({ sender: username, content: input, recipient }));
      ////console.log('ChatApp.js: USERNAME', username);
      
      // 2. Envoi de la notification au backend notifications
      if (recipient && recipient !== username) {
        try {
          const notifWs = new window.WebSocket(getNotifWsUrl());
          notifWs.onopen = () => {
            notifWs.send(JSON.stringify({ user: username })); // identification obligatoire
            setTimeout(() => {
              notifWs.send(JSON.stringify({
                title: `Message de ${username}`,
                message: input,
                user: recipient // cible le destinataire
              }));
              notifWs.close();
            }, 100); // petit délai pour laisser le backend enregistrer le client
          };

        } catch (e) {
         // //console.error('Erreur envoi notification via ' + getNotifWsUrl(), e);
         //console.error('Erreur envoi notification');
        }
      }
      setInput("");
    } else {
      setMessages(msgs => [...msgs, { sender: 'SYSTEM', content: 'Connexion au serveur de chat non établie. Réessayez dans quelques secondes.', timestamp: Date.now() }]);
    }
  };

  // --- Appel vidéo/audio ---
  const startCall = async (target, video = true) => {
    setCallError("");
    try {
      setCallTarget(target);
      setIsVideoCall(video);
      setCallDialogOpen(true);
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video, audio: true });
      } catch (err) {
        if (video) {
          // Fallback audio-only si webcam absente
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
            setIsVideoCall(false);
            setCallError("Webcam non trouvée, passage en mode audio uniquement.");
          } catch (err2) {
            setCallDialogOpen(false);
            setCallError("Aucun périphérique audio/vidéo trouvé ou accès refusé.");
            return;
          }
        } else {
          setCallDialogOpen(false);
          setCallError("Aucun périphérique audio trouvé ou accès refusé.");
          return;
        }
      }
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          ws.current.send(JSON.stringify({ type: 'webrtc-ice', candidate: event.candidate, recipient: target }));
        }
      };
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.current.send(JSON.stringify({ type: 'webrtc-offer', offer, recipient: target }));
    } catch (err) {
      setCallDialogOpen(false);
      setCallError("Erreur lors de l'initialisation de l'appel : " + (err && err.message ? err.message : err));
    }
  };

  const handleReceiveOffer = async (msg) => {
    setCallTarget(msg.sender);
    setIsVideoCall(true);
    setCallDialogOpen(true);
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;
    const pc = new RTCPeerConnection();
    peerConnectionRef.current = pc;
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.current.send(JSON.stringify({ type: 'webrtc-ice', candidate: event.candidate, recipient: msg.sender }));
      }
    };
    pc.ontrack = (event) => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
    };
    await pc.setRemoteDescription(new RTCSessionDescription(msg.offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    ws.current.send(JSON.stringify({ type: 'webrtc-answer', answer, recipient: msg.sender }));
  };

  const handleReceiveAnswer = async (msg) => {
    if (peerConnectionRef.current) {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(msg.answer));
    }
  };

  const handleReceiveIce = async (msg) => {
    if (peerConnectionRef.current) {
      try {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } catch (e) {}
    }
  };

  const endCall = () => {
    setCallDialogOpen(false);
    setCallTarget(null);
    setIsVideoCall(false);
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
      remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      remoteVideoRef.current.srcObject = null;
    }
  };

  const showNotification = async (sender) => {
    if (!("Notification" in window)) {
      //console.log("Notifications API not supported");
      return;
    }

    // try {
     // const permission = await Notification.requestPermission();
    //  if (permission === "granted") {
    //    new Notification(`Nouveau message de ${sender}`, {
    //      icon: '/favicon.ico',
    //      vibrate: [200, 100, 200]
    //    });
    //  }
   //  } catch (error) {
   //   //console.error("Notification error:", error);
   // }
  };

  if (tokenExpired) {
    return (
      <Paper elevation={4} sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
        <Typography variant="h6" align="center" gutterBottom>
          Session expirée
        </Typography>
        <Typography align="center" color="error" sx={{ mb: 2 }}>
          Votre session a expiré. Veuillez vous reconnecter pour continuer à utiliser le chat.
        </Typography>
        {/* Ajouter ici un bouton pour relancer la connexion ou ouvrir la page de login */}
      </Paper>
    );
  }

  // DEBUG : affiche tous les messages reçus pour aider au diagnostic
  ////console.log('MESSAGES:', messages);

  // Utilitaire pour comparer utilisateurs (username ou id)
  function userEquals(u1, u2) {
    return u1 === u2 || (u1?.toString().toLowerCase() === u2?.toString().toLowerCase());
  }

  return (
    <Paper elevation={4} sx={{ p: 3, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <Typography variant="h6" align="center" gutterBottom>
        Chat Webdesk
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
        <Box sx={{ 
          width: 250,
          bgcolor: '#f3f3fa', 
          borderRadius: 2, 
          p: 1, 
          mr: 2, 
          flexShrink: 0, 
          height: '100%', 
          overflowY: 'auto' 
        }}>
          <Typography variant="subtitle2">Utilisateurs connectés</Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Rechercher..."
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
          />
          <List dense sx={{ 
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto', 
            border: '1px solid #eee', 
            borderRadius: 1,
            bgcolor: 'white',
            p: 1
          }}>
            {(() => {
  // Récupère le username courant depuis localStorage
  let usernameCourant = null;
  try {
    const userStr = window.localStorage.getItem('user');
    if (userStr) {
      const userObj = JSON.parse(userStr);
      usernameCourant = userObj.username || null;
    }
  } catch {}
  return users
    .filter(u => u.username !== usernameCourant && (u.username || u.id || '').toLowerCase().includes(userSearch.toLowerCase()))
    .map(u => {
      ////console.log('User in list:', u);
      return (
        <ListItem
          key={u.id || u.username}
          button
          onClick={() => {
            setRecipient(u.username || u.id);
            setShowConversation(true);
          }}
          sx={{
            bgcolor: recipient === (u.username || u.id) ? '#e3f2fd' : 'inherit',
            '&:hover': { bgcolor: '#f5f5f5' },
            mb: 1,
            borderRadius: 1
          }}
          secondaryAction={
            <>
              <IconButton size="small" onClick={() => startCall(u.username || u.id, false)} title="Appel audio"><CallIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => startCall(u.username || u.id, true)} title="Appel vidéo"><VideocamIcon fontSize="small" /></IconButton>
            </>
          }
        >
          <ListItemText primary={u.username || u.id} />
        </ListItem>
      );
    })
  })()}
          </List>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}>
          {showConversation && recipient && (
            <Box sx={{ 
              flex: 1,
              borderLeft: '1px solid #eee',
              p: 2,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Affichage type Teams/WhatsApp : une seule fenêtre de conversation active, sélectionnée à gauche */}
              {recipient ? (
                (() => {
                  const conv = messages
                    .filter(m => 
                      userEquals(m.recipient, recipient) || 
                      userEquals(m.sender, recipient)
                    )
                    // Élimine les doublons
                    .filter((msg, index, self) =>
                      index === self.findIndex(m => 
                        m.timestamp === msg.timestamp && 
                        userEquals(m.sender, msg.sender)
                      )
                    );

                  const messageStyle = {
                    system: { 
                      bgcolor: 'error.light', 
                      color: 'error.contrastText',
                      textAlign: 'center',
                      mx: 'auto',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      maxWidth: '90%',
                      border: 'none'
                    },
                    fromMe: { 
                      bgcolor: 'rgba(200, 230, 255, 0.8)', 
                      textAlign: 'right',
                      color: 'rgba(0, 0, 0, 0.87)',
                      borderRadius: '18px 18px 4px 18px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        bgcolor: 'rgba(200, 230, 255, 0.9)'
                      }
                    },
                    fromOther: { 
                      bgcolor: 'background.paper',
                      textAlign: 'left',
                      borderRadius: '18px 18px 18px 4px',
                      border: '1px solid',
                      borderColor: 'divider'
                    }
                  };

                  return (
                    <Box sx={{ 
                      border: '1px solid #e0e0e0',
                      p: 2,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 300,
                      maxHeight: { xs: '80vh', sm: 'calc(100vh - 200px)' },
                      boxSizing: 'border-box',
                      borderRadius: 1,
                      backgroundColor: '#fafafa',
                      overflow: 'hidden',
                      width: '100%',
                      maxWidth: '100%',
                      '& > *': {
                        maxWidth: '100%',
                        boxSizing: 'border-box'
                      }
                    }}>
                      <Typography variant="h6" color="primary">
                        Chat avec {recipient}
                      </Typography>
                      
                      <Box 
                        ref={listRef}
                        sx={{ 
                          flex: 1, 
                          minHeight: 0, 
                          overflowY: 'auto', 
                          p: 1,
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        {conv.map((msg, idx) => {
                          const style = msg.sender === 'SYSTEM' 
                            ? messageStyle.system
                            : msg.sender === recipient
                              ? messageStyle.fromMe
                              : messageStyle.fromOther;
                          
                          return (
                            <Box
                              key={idx}
                              sx={{
                                p: 1.5,
                                mb: 1.5,
                                borderRadius: 2,
                                maxWidth: { xs: '100%', sm: '90%', md: '80%' },
                                width: 'fit-content',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'break-word',
                                boxSizing: 'border-box',
                                ml: style.textAlign === 'right' ? 'auto' : 0,
                                mr: style.textAlign === 'left' ? 'auto' : 0,
                                ...style,
                                '&:hover': {
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                              }}>
                                <Box sx={{ 
                                  display: 'flex',
                                  justifyContent: style.textAlign === 'right' ? 'flex-end' : 'flex-start',
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 'medium',
                                      color: style.color || 'text.primary',
                                      fontSize: '0.75rem',
                                      opacity: 0.9
                                    }}
                                  >
                                    {msg.sender}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      opacity: 0.6,
                                      lineHeight: 1
                                    }}
                                  >
                                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                  </Typography>
                                </Box>
                                <Typography 
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.4,
                                    fontSize: '0.9rem',
                                    color: style.color || 'text.primary'
                                  }}
                                >
                                  {msg.content}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Votre message..."
                          value={input}
                          fullWidth
                          onChange={e => setInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                        />
                        <Button variant="contained" onClick={sendMessage}>Envoyer</Button>
                      </Box>
                    </Box>
                  );
                })()
              ) : (
                <Box sx={{ color: '#888', textAlign: 'center', mt: 4, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography>Veuillez sélectionner un utilisateur pour afficher la conversation.</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Dialog open={callDialogOpen} onClose={endCall} maxWidth="md" fullWidth>
        <DialogTitle>{isVideoCall ? 'Appel vidéo' : 'Appel audio'} avec {callTarget}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <video ref={localVideoRef} autoPlay muted playsInline style={{ width: 200, borderRadius: 8, background: '#222' }} />
            <video ref={remoteVideoRef} autoPlay playsInline style={{ width: 200, borderRadius: 8, background: '#222' }} />
          </Box>
          <Button onClick={endCall} variant="contained" color="error" sx={{ mt: 2 }}>Raccrocher</Button>
        </DialogContent>
      </Dialog>
      {callError && (
        <Box sx={{ mt: 2 }}>
          <Typography color="error" align="center">{callError}</Typography>
        </Box>
      )}
    </Paper>
  );
}
